/* import __COLOCATED_TEMPLATE__ from './performance.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import { HEIGHT_TYPES, WIDTH_TYPES } from 'embercom/models/reporting/custom/chart';
import { VISUALIZATION_TYPES } from 'embercom/models/reporting/custom/visualization-options';
import { tracked } from '@glimmer/tracking';
import PALETTE from '@intercom/pulse/lib/palette';
import { filterableProperties as defaultFilterableProperties } from 'embercom/lib/reporting/custom/filter-helpers';
import type ConversationAttributeDescriptor from 'embercom/models/conversation-attributes/descriptor';
import { FinPerformanceFunnelConfig } from 'embercom/components/reporting/bespoke/fin-performance/conversation-funnel/series-config';
import type AiAgentSetupService from 'embercom/services/ai-agent-setup-service';
import ENV from 'embercom/config/environment';
import { getAllowedFilterableAttributeIdsForStandalone } from 'embercom/services/reporting-standalone';
import type ReportingChartService from 'embercom/services/reporting-chart-service';
import { type StandalonePlatform } from 'embercom/objects/standalone/constants';

const DEFAULT_CHART_DEFINITION = {
  id: null,
  title: null,
  visualization_type: null,
  metric_property: null,
  metric_function: null,
  stacked: false,
  view_by: null,
  view_by_time_interval: null,
  view_by_display_limit: 10,
  view_by_display_other: false,
  width_type: null,
  height_type: null,
};

export const CHART_SERIES_COLORS = {
  finInvolved: PALETTE['vis-blue-50'],
  finResolved: PALETTE['vis-mint-50'],
  customerExperience: PALETTE['vis-violet-50'],
};

interface Args {
  conversationAttributeDescriptors: ConversationAttributeDescriptor[];
}

export default class Performance extends Component<Args> {
  @service declare appService: AppService;
  @service declare intl: IntlService;
  @service declare aiAgentSetupService: AiAgentSetupService;
  @service declare reportingChartService: ReportingChartService;
  @tracked impactOverTimeMetricConfig = 'percentage';
  @tracked showStandaloneSideDrawer = false;

  get serializedReport() {
    return {
      id: 'fin-performance',
      title: null,
      description: null,
      charts: [],
      filters: {},
      date_range: {
        selection: 'past_4_weeks',
      },
    };
  }

  get filterableProperties() {
    if (this.appService.app.isStandaloneApp) {
      return getAllowedFilterableAttributeIdsForStandalone(this.appService);
    }

    return defaultFilterableProperties().concat(
      'teammate',
      'team',
      'conversation.replied.workflow_ids',
    );
  }

  get performanceOverviewCharts() {
    return [
      {
        ...DEFAULT_CHART_DEFINITION,
        id: 'involvement_rate',
        title: this.intl.t('ai-agent.analyze.performance.involvement-rate'),
        visualization_type: VISUALIZATION_TYPES.COUNTER,
        visualization_options: {
          show_time_comparison: true,
        },
        width_type: WIDTH_TYPES.TWO_COLUMN,
        height_type: HEIGHT_TYPES.X_SMALL,
        chart_series: [
          {
            metric_id: 'fin.participation_rate',
            show_data_labels: true,
          },
        ],
      },
      {
        ...DEFAULT_CHART_DEFINITION,
        id: 'resolution_rate',
        title: this.intl.t('ai-agent.analyze.performance.resolution-rate'),
        visualization_type: VISUALIZATION_TYPES.COUNTER,
        visualization_options: {
          show_time_comparison: true,
        },
        width_type: WIDTH_TYPES.TWO_COLUMN,
        height_type: HEIGHT_TYPES.X_SMALL,
        chart_series: [
          {
            metric_id: 'fin.resolution_rate',
            show_data_labels: true,
          },
        ],
      },
      {
        ...DEFAULT_CHART_DEFINITION,
        id: 'cx_score',
        title: this.intl.t('ai-agent.analyze.performance.cx-score'),
        visualization_type: VISUALIZATION_TYPES.COUNTER,
        visualization_options: {
          show_time_comparison: true,
        },
        width_type: WIDTH_TYPES.TWO_COLUMN,
        height_type: HEIGHT_TYPES.X_SMALL,
        chart_series: [
          {
            metric_id:
              'conversation.ai_generated_metrics.csat_score.first_user_conversation_part_created_at',
            show_data_labels: true,
            filters: {
              type: 'and',
              filters: [
                {
                  type: 'category',
                  data: {
                    property: 'agent_types_involved_grouping',
                    attribute: 'conversation.agent_types_involved_grouping',
                    values: ['ai-agent'],
                  },
                },
              ],
            },
          },
        ],
      },
    ];
  }

  get performanceOverTimeBaseChart() {
    return {
      ...DEFAULT_CHART_DEFINITION,
      id: 'fin_performance_over_time_absolute',
      visualization_type: VISUALIZATION_TYPES.LINE,
      width_type: WIDTH_TYPES.FULL,
      height_type: HEIGHT_TYPES.TALL,
      view_by: 'time',
      show_default_chart_title: false,
    };
  }

  get absolutePerformanceOverTimeChart() {
    return [
      {
        ...this.performanceOverTimeBaseChart,
        chart_series: [
          {
            metric_id: 'fin.conversations_participated.count',
            label: this.intl.t('ai-agent.analyze.performance.fin-involvements'),
            color: CHART_SERIES_COLORS.finInvolved,
          },
          {
            metric_id: 'fin.conversations_resolved.count',
            label: this.intl.t('ai-agent.analyze.performance.fin-resolutions'),
            color: CHART_SERIES_COLORS.finResolved,
          },
          {
            metric_id: 'conversation.ai_generated_metrics.csat.positively_rated.count',
            label: this.intl.t('ai-agent.analyze.performance.positive-fin-experiences'),
            filters: {
              type: 'and',
              filters: [
                {
                  type: 'category',
                  data: {
                    property: 'agent_types_involved_grouping',
                    attribute: 'conversation.agent_types_involved_grouping',
                    values: ['ai-agent'],
                  },
                },
              ],
            },
            color: CHART_SERIES_COLORS.customerExperience,
          },
        ],
      },
    ];
  }

  get percentagePerformanceOverTimeChart() {
    return [
      {
        ...this.performanceOverTimeBaseChart,
        chart_series: [
          {
            metric_id: 'fin.participation_rate',
            label: this.intl.t('ai-agent.analyze.performance.involvement-rate'),
            color: CHART_SERIES_COLORS.finInvolved,
          },
          {
            metric_id: 'fin.resolution_rate',
            label: this.intl.t('ai-agent.analyze.performance.resolution-rate'),
            color: CHART_SERIES_COLORS.finResolved,
          },
          {
            metric_id:
              'conversation.ai_generated_metrics.csat_score.first_user_conversation_part_created_at',
            label: this.intl.t('ai-agent.analyze.performance.cx-score'),
            filters: {
              type: 'and',
              filters: [
                {
                  type: 'category',
                  data: {
                    property: 'agent_types_involved_grouping',
                    attribute: 'conversation.agent_types_involved_grouping',
                    values: ['ai-agent'],
                  },
                },
              ],
            },
            color: CHART_SERIES_COLORS.customerExperience,
          },
        ],
      },
    ];
  }

  get conversationFunnelChart() {
    return {
      ...DEFAULT_CHART_DEFINITION,
      id: 'fin_performance.conversation_funnel',
      title: this.intl.t('reporting.overview.charts.handling.funnel.title'),
      show_default_chart_title: false,
      description: this.intl.t('reporting.overview.charts.handling.funnel.description'),
      chart_series: FinPerformanceFunnelConfig.getChartSeriesWithLabels(this.intl),
      width_type: WIDTH_TYPES.FULL,
      height_type: HEIGHT_TYPES.X_TALL,
      visualization_type: VISUALIZATION_TYPES.BESPOKE,
      component_name: 'fin-performance/conversation-funnel',
    };
  }

  get hasFinLive() {
    if (ENV.environment === 'development') {
      return true;
    } else if (this.appService.app.isStandaloneApp) {
      return this.appService.mainStandalonePlatform.finIsLive;
    }
    return this.aiAgentSetupService.isFinLiveIncludingChannelsOutsideSetup();
  }

  get emptyState() {
    if (this.appService.app.canUseFeature('insights-override-fin-live-empty-states')) {
      return null;
    }
    if (this.shouldShowZendeskConnectScreen) {
      return 'zendesk';
    } else if (this.shouldShowSalesforceConnectScreen) {
      return 'salesforce';
    } else if (this.finIsNotLiveAndIsPaused) {
      return 'fin-paused';
    } else if (!this.hasFinLive) {
      return 'fin-not-live';
    } else {
      return null; // not an empty state - show it!
    }
  }

  get finIsNotLiveAndIsPaused() {
    if (ENV.environment === 'development' || !this.appService.app.isStandaloneApp) {
      return false;
    }
    return this.appService.mainStandalonePlatform.finIsNotLiveAndIsPaused;
  }

  get shouldShowReportFilters() {
    return (
      this.hasFinLive &&
      !this.shouldShowZendeskConnectScreen &&
      !this.shouldShowSalesforceConnectScreen
    );
  }

  get shouldShowZendeskConnectScreen() {
    if (!this.appService.app.isStandaloneApp || this.reportingChartService.useSyntheticData) {
      return false;
    }

    return (
      this.appService.app.hasStandalonePlatform('zendesk') &&
      this.appService.mainStandalonePlatform.showAnalyzeSectionConnectCta
    );
  }

  get shouldShowSalesforceConnectScreen() {
    if (!this.appService.app.isStandaloneApp || this.reportingChartService.useSyntheticData) {
      return false;
    }

    return (
      this.appService.app.hasStandalonePlatform('salesforce') &&
      this.appService.mainStandalonePlatform.showAnalyzeSectionConnectCta
    );
  }

  get currentPlatform(): StandalonePlatform {
    return this.appService.mainStandalonePlatform.platformKey;
  }

  @action
  closeStandaloneSideDrawer() {
    this.showStandaloneSideDrawer = false;
  }

  @action
  openStandaloneSideDrawer() {
    this.showStandaloneSideDrawer = true;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAutomation::Analyze::Performance': typeof Performance;
    'ai-automation/analyze/Performance': typeof Performance;
  }
}
